import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import signatureService from '../../services/signature/SignatureService'
import RegistrasiService from '../../services/registrasi/RegistrasiService'
import Swal from 'sweetalert2'
import NotificationService from '../../services/notification/NotificationService'

const stageLabel = stage => stage === 'APPROVAL' ? 'Approval' : stage === 'FINAL' ? 'Final' : stage || '-'

export default function SignaturePage() {
    const { documentId } = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const notification = location.state?.notification
    const [document, setDocument] = useState(null)
    const [loading, setLoading] = useState(true)
    const [passphrase, setPassphrase] = useState('')
    const [reason, setReason] = useState('Persetujuan Dokumen')
    const [locationText, setLocationText] = useState('SIAPMUTU')
    const [showConfirm, setShowConfirm] = useState(false)
    const [signing, setSigning] = useState(false)
    const [result, setResult] = useState(null)
    const [error, setError] = useState('')
    const [certificateChecking, setCertificateChecking] = useState(true)

    let notificationService = useMemo(() => new NotificationService(), [])

    useEffect(() => {
        let active = true

        const load = async () => {
            try {
                // Cek status dokumen terlebih dahulu. Dokumen SIGNED tidak boleh
                // kembali masuk ke halaman proses TTE.
                const signatureStatus = await signatureService.getStatus(documentId)

                if (signatureStatus?.data?.signed || signatureStatus?.data?.status === 'SIGNED') {
                    if (active) {
                        navigate(`/signature/${documentId}/signed`, { replace: true })
                    }
                    return
                }

                const certRes = await new RegistrasiService().checkSignatureCertificate()
                const cert = certRes?.data || {}
                const status = String(cert.statusCert ?? cert.status ?? '').toUpperCase()
                const expired = cert.expiredDate && new Date(cert.expiredDate).getTime() <= Date.now()
                const ready = Boolean(cert.id) && !expired && ['1', 'ACTIVE', 'VALID', 'AKTIF'].includes(status)

                if (!ready) {
                    if (active) {
                        setError('Certificate tanda tangan belum tersedia atau sudah tidak valid. Silakan buat/perbarui certificate terlebih dahulu.')
                        navigate('/signature/certificate', {
                            replace: true,
                            state: { fromSignature: true, documentId }
                        })
                    }
                    return
                }

                const res = await notificationService.getDocument(documentId)
                    if (active) {
                    setDocument(res.data)
                    }
            } catch (e) {
                if (active) {
                    setError(e?.response?.data?.message || 'Status dokumen atau certificate tidak dapat diperiksa. Silakan coba lagi.')
                }
            } finally {
                if (active) {
                    setCertificateChecking(false)
                    setLoading(false)
                }
            }
        }

        load()

        return () => {
            active = false
            }
    }, [documentId, navigate, notificationService])

    const previewUrl = useMemo(() => document?.dokPath || '', [document])

    const sign = async () => {
        if (!passphrase) { setError('Passphrase wajib diisi.'); return }
        setSigning(true); setError('')
        try {
            const res = await signatureService.sign(documentId, { passphrase, reason, location: locationText })
            setPassphrase('')
            setShowConfirm(false)
            setResult(res.data)

            await Swal.fire({
                icon: 'success',
                title: 'Tanda tangan berhasil',
                text: 'Dokumen berhasil ditandatangani secara digital.',
                confirmButtonText: 'Lanjut',
                confirmButtonColor: '#0d6efd'
            })
        } catch (e) {
            const status = e?.response?.status
            const message = e?.response?.data?.message
                || e?.response?.data?.keterangan
                || (status === 401
                    ? 'Passphrase certificate tidak benar.'
                    : 'Tanda tangan digital gagal diproses.')

            await Swal.fire({
                icon: 'error',
                title: 'Tanda tangan gagal',
                text: message,
                confirmButtonText: 'Tutup',
                confirmButtonColor: '#dc3545'
            })

            setError(message)
        } finally { setSigning(false) }
    }

    const downloadSigned = async () => {
        try {
            const res = await signatureService.downloadSigned(documentId)
            const blobUrl = URL.createObjectURL(res.data)
            const a = document.createElement('a'); a.href = blobUrl; a.download = `signed-${documentId}.pdf`; a.click()
            URL.revokeObjectURL(blobUrl)
        } catch (_) { setError('PDF signed tidak dapat diunduh.') }
    }

    if (certificateChecking) return <div className="text-center py-5"><Spinner animation="border" /><div className="mt-2 text-muted">Memeriksa certificate...</div></div>

    if (loading) return <div className="text-center py-5"><Spinner animation="border" /></div>

    return (
        <div className="container-fluid py-4">
            <Card className="mb-4 bg-transparent border-0 shadow-none">
                <Card.Body className="px-1 py-0">
                    <Row className="align-items-end">
                        <Col md={6}>
                            <h3 className="text-white fw-bold mb-2">
                                Tanda Tangan Digital
                            </h3>
                            <div className="text-white">
                                Pastikan dokumen sudah benar sebelum menandatangani.
                            </div>
                        </Col>

                        <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0">
                            <Button variant="warning" onClick={() => navigate(-1)}>Kembali</Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
            {result ?
                <Card>
                    <Card.Body>
                        <Alert variant="success"><strong>Dokumen berhasil ditandatangani.</strong><br />Kode verifikasi: {result.verificationCode || '-'}</Alert>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button onClick={downloadSigned}>Unduh PDF Signed</Button>
                            <Button
                                variant="success"
                                onClick={() => navigate(`/signature/${documentId}/signed`)}
                            >
                                Lihat Dokumen Signed
                            </Button>
                            {result.verificationCode &&
                                <Button
                                    variant="outline-primary"
                                    href={`${process.env.REACT_APP_PUBLIC_VERIFICATION_URL || '/verify'}/${result.verificationCode}`}
                                    target="_blank"
                                >
                                    Verifikasi
                                </Button>
                            }
                        </div>
                    </Card.Body>
                </Card> :
                <Card>
                    <Card.Body>
                        <h5>{notification?.title || 'Dokumen menunggu tanda tangan'}</h5>
                        <div className="row mt-3">
                            <div className="col-md-6">
                                <dl>
                                    <dt>Dokumen ID</dt>
                                    <dd>{documentId}</dd>
                                    <dt>Nomor Dokumen</dt>
                                    <dd>{document?.noHcMutu || document?.documentNumber || '-'}</dd>
                                    <dt>Nama File</dt>
                                    <dd>{document?.dokName || '-'}</dd>
                                    <dt>Tahap</dt>
                                    <dd>{stageLabel(notification?.signatureStage || notification?.stage)}</dd>
                                </dl>
                            </div>
                            <div className="col-md-6">
                                <Alert variant="warning">Passphrase certificate hanya digunakan untuk proses signing dan tidak disimpan oleh aplikasi.</Alert>
                            </div>
                        </div>
                        {previewUrl ?
                            <iframe title="Preview PDF" src={previewUrl} style={{ width: '100%', height: '65vh', border: '1px solid #ddd' }} />
                            :
                            <Alert variant="info">Preview dokumen belum tersedia dari endpoint transaksi.</Alert>}

                        <div className="mt-3 d-flex justify-content-end">
                            <Button variant="primary" onClick={() => setShowConfirm(true)}>Tanda Tangani Dokumen</Button>
                        </div>
                    </Card.Body>
                </Card>
            }

            <Modal
                show={showConfirm}
                onHide={() => !signing && setShowConfirm(false)}
                centered
            >
                <Modal.Header closeButton={!signing}>
                    <Modal.Title>Konfirmasi Tanda Tangan</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning">Anda akan menandatangani dokumen <strong>{document?.noHcMutu || documentId}</strong>.</Alert>
                    <Form.Group className="mb-3">
                        <Form.Label>Passphrase Certificate</Form.Label>
                        <Form.Control type="password" autoComplete="new-password" value={passphrase} onChange={e => setPassphrase(e.target.value)} disabled={signing} autoFocus />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Reason</Form.Label>
                        <Form.Control value={reason} onChange={e => setReason(e.target.value)} disabled={signing} />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Location</Form.Label>
                        <Form.Control value={locationText} onChange={e => setLocationText(e.target.value)} disabled={signing} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirm(false)} disabled={signing}>Batal</Button>
                    <Button variant="primary" onClick={sign} disabled={signing || !passphrase}>
                        {signing ?
                            <><Spinner size="sm" className="me-2" />Memproses...</> : 'Ya, Tanda Tangani'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}
